import { Download } from "lucide-react";
import type { PublicQuote } from "@forgepoint/shared";
import { BackButton } from "@/components/BackButton";
import { Button } from "@/components/ui/button";
import { QuoteDocumentRenderer } from "./QuoteDocumentRenderer";
import { ViewToggle } from "./ViewToggle";
import type { DocumentAssetUrls } from "./types";

/**
 * Print resets for the preview's Download PDF path, mirroring the customer-
 * facing public quote page: white page background and zero body margins so
 * only the PdfPageViewer's letter-sized sheets reach the printer. PdfPageViewer
 * itself emits the @page size/margin and per-page break rules.
 *
 * The preview lives inside a full-screen, position-fixed dialog. Chrome prints
 * a fixed element once, clipped to the first page, so the dialog is flattened
 * into normal flow and every other body child (the app behind the dialog, the
 * dialog's overlay, any open sheet - Radix portals each one directly under
 * body) is hidden.
 */
const PREVIEW_PRINT_STYLES = `
  @media print {
    html,
    body {
      background: white !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }
    body > :not(:has([data-document-preview])) {
      display: none !important;
    }
    [role="dialog"]:has([data-document-preview]) {
      position: static !important;
      height: auto !important;
      overflow: visible !important;
    }
  }
`;

interface DocumentPreviewScreenProps {
  /** The document HTML to render without editing chrome. */
  documentHtml: string;
  /** The quote projection the document binds. */
  quote: PublicQuote;
  /** Resolved signed asset urls keyed by asset id. */
  assetUrls: DocumentAssetUrls;
  /** The org logo url, when it is not the bound quote's own. */
  orgLogoUrl?: string | null;
  /** The hint shown beside the back button. */
  hint: string;
  /**
   * Label for the exit control, rendered as a plain button. Omit to render the
   * editor's Edit/Preview toggle instead.
   */
  exitLabel?: string;
  /** Returns to the editing view. */
  onExitPreview: () => void;
  /** Closes the editor entirely. */
  onRequestClose: () => void;
}

/**
 * The editor's preview screen: the document rendered exactly as the customer
 * sees it, with a print path. Shared by the per-quote and template editors.
 * @param documentHtml - The document HTML to render without editing chrome
 * @param quote - The quote projection the document binds
 * @param assetUrls - Resolved signed asset urls keyed by asset id
 * @param orgLogoUrl - The org logo url, overriding the bound quote's own
 * @param hint - The hint shown beside the back button
 * @param exitLabel - Label for a plain exit button, or omit for the view toggle
 * @param onExitPreview - Returns to the editing view
 * @param onRequestClose - Closes the editor entirely
 * @returns The preview screen
 */
export function DocumentPreviewScreen({
  documentHtml,
  quote,
  assetUrls,
  orgLogoUrl,
  hint,
  exitLabel,
  onExitPreview,
  onRequestClose,
}: DocumentPreviewScreenProps) {
  return (
    <div
      data-document-preview
      className="flex h-full flex-col print:block print:h-auto"
    >
      <header className="flex items-center gap-3 border-b bg-background px-4 py-2 shrink-0 print:hidden">
        <BackButton onClick={onRequestClose} />
        <span className="text-xs text-muted-foreground">{hint}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Download className="size-4" /> Download PDF
          </Button>
          {exitLabel ? (
            <Button variant="outline" size="sm" onClick={onExitPreview}>
              {exitLabel}
            </Button>
          ) : (
            <ViewToggle isPreview onChange={() => onExitPreview()} />
          )}
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-auto bg-muted print:h-auto print:overflow-visible print:bg-white">
        <style dangerouslySetInnerHTML={{ __html: PREVIEW_PRINT_STYLES }} />
        <QuoteDocumentRenderer
          documentHtml={documentHtml}
          quote={quote}
          assetUrls={assetUrls}
          orgLogoUrl={orgLogoUrl}
          mode="view"
        />
      </div>
    </div>
  );
}
